/**
 * The follower (D238) — a body that goes where the player goes
 * (`docs/design/the-meridian.md` §9.1; Lyra's engine read of 2026-10-08,
 * §1; `docs/design/the-meridian-act-1.md` §6.2 and §13.1, TALLY).
 *
 * @remarks
 * D233 gave a body one way to change rooms, `move_actor`, and deferred "the
 * thing that follows" by name: a trigger per step was the mechanism. Faked
 * that way a follower costs a trigger per room, hears only `go` (never a
 * forced move), speaks after the room's page, and — because a trigger sees
 * the world AFTER the move — cannot ask "was it in the room I just left?",
 * which on a ring teleports it. So `follows` is one field on the actor, read
 * at the one seam every arrival already passes through (`enterRoom`), and
 * it stores nothing: where a body stands is already the world's
 * (`world.actorLocations`), and whether it follows is a condition.
 *
 * The shape, and why each part is there:
 *
 * - **`while`** — the condition under which the body goes where the player
 *   goes, judged in the doorway, as the player leaves. Absent, it always
 *   does. While it holds, a follower standing in the room the player leaves
 *   arrives with them, SILENTLY: it is with you, and that is not news — its
 *   presence is the room's own `actor_present` fragment, read on the page
 *   the arrival prints, because the follower is moved before the page is.
 * - **`refuses`** — rooms it will not enter, each arm with an optional
 *   `when` (absent: never) and the lines the player sees as it stops at the
 *   threshold. The first arm that names the room and holds is the one that
 *   speaks, once, in the turn the player goes on without it; the body stays
 *   where it stood. TALLY and the vacuum is a `when`; TALLY and
 *   Observation's rail-less floor is an arm without one.
 * - **`rejoinNarratives`** — what the player sees when they come back into
 *   a room where the follower waits (from another room, while `while`
 *   holds). Absent, the rejoin is as silent as the follow: the next doorway
 *   simply finds it at their side, because a follower follows from wherever
 *   the player finds it — nothing records that it was ever left.
 *
 * What a follower is NOT, refused here rather than defined badly:
 *
 * - **A body in the way.** `blocksExits` beside `follows` would hold every
 *   door the follower stands at, which is every door the player stands at.
 *   A following body never blocks the player's own way.
 * - **A foe.** D234's promise is that running works — "a foe that is no
 *   longer present when the tick comes round has nobody to strike". A foe
 *   that follows breaks it; the stalker is its own primitive, still
 *   deferred.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { conditionSchema } from './conditions.js';
import { roomIdSchema } from './identifiers.js';
import { narrativeLinesSchema } from './transcript.js';

/**
 * One refusal arm — rooms the follower will not enter, when, and what the
 * player sees as it stops.
 */
export const followRefusalSchema = z
	.strictObject({
		/** What the player sees as the follower stops at the threshold — spoken once, in the turn the player goes on without it, before the new room's page. */
		narratives: narrativeLinesSchema,
		/** The rooms this arm refuses — each a room the pack declares (the loader says so). */
		roomIds: z.array(roomIdSchema).min(1).readonly(),
		/** Judged in the doorway, as the player leaves. Absent, the arm always holds: the room is never entered. */
		when: conditionSchema.optional(),
	})
	.readonly()
	.refine((refusal) => new Set(refusal.roomIds).size === refusal.roomIds.length, {
		message: 'a refusal lists the same room twice',
		path: ['roomIds'],
	});

/** One refusal arm — see {@link followRefusalSchema}. */
export type FollowRefusal = z.infer<typeof followRefusalSchema>;

/** A body that goes where the player goes — see the module remarks. */
export const actorFollowsSchema = z
	.strictObject({
		/** The rooms it will not enter, first matching arm wins. */
		refuses: z.array(followRefusalSchema).readonly().default([]),
		/** What the player sees on coming back into a room where it waits. Absent, the rejoin is silent. */
		rejoinNarratives: narrativeLinesSchema.optional(),
		/** While this holds, the body goes where the player goes. Judged in the doorway; absent, it always does. */
		while: conditionSchema.optional(),
	})
	.readonly();

/** A body that goes where the player goes — see {@link actorFollowsSchema}. */
export type ActorFollows = z.infer<typeof actorFollowsSchema>;
